import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "../../styles/admin-dashboard.css";

const API_BASE_URL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5000"
    : process.env.REACT_APP_API_BASE_URL ||
      "https://apti-backend-8fhm.onrender.com";

const ADMIN_API_KEY = process.env.REACT_APP_ADMIN_API_KEY;

const PERSONALITY_LABELS = {
  analytical_thinker: "Analytical Thinker",
  versatile_collaborator: "Versatile Collaborator",
  strategic_planner: "Strategic Planner",
  creative_explorer: "Creative Explorer",
  practical_operator: "Practical Operator",
  independent_problem_solver: "Independent Problem Solver"
};

function formatDate(value) {
  if (!value) return "Unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unavailable";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric"
  });
}

function personalityLabel(value) {
  if (!value || typeof value !== "string") return "Unavailable";
  return PERSONALITY_LABELS[value] || value.replace(/_/g, " ");
}

function recommendationLabel(value) {
  return typeof value === "string" && value.trim()
    ? value.trim()
    : "Unavailable";
}

export default function DashboardOverview() {
  const [programmeState, setProgrammeState] = useState({
    loading: true,
    data: [],
    error: ""
  });
  const [assessmentState, setAssessmentState] = useState({
    loading: true,
    data: [],
    error: ""
  });

  useEffect(() => {
    if (!ADMIN_API_KEY) {
      const missingKey = "The Admin university API key is not configured.";
      setProgrammeState({ loading: false, data: [], error: missingKey });
      setAssessmentState({ loading: false, data: [], error: missingKey });
      return undefined;
    }

    const controller = new AbortController();

    async function loadScopedData(path, setState, label) {
      try {
        const response = await fetch(`${API_BASE_URL}${path}`, {
          headers: { "x-api-key": ADMIN_API_KEY },
          signal: controller.signal
        });
        const payload = await response.json().catch(() => ({}));

        if (response.status === 401) {
          throw new Error("The Admin university API key is invalid.");
        }
        if (!response.ok) {
          throw new Error(payload.error || `${label} could not be loaded.`);
        }

        setState({
          loading: false,
          data: Array.isArray(payload.data) ? payload.data : [],
          error: ""
        });
      } catch (error) {
        if (error.name !== "AbortError") {
          setState({
            loading: false,
            data: [],
            error: error.message || `${label} could not be loaded.`
          });
        }
      }
    }

    loadScopedData("/api/programs", setProgrammeState, "Programme data");
    loadScopedData("/api/results", setAssessmentState, "Assessment activity");

    return () => controller.abort();
  }, []);

  const programmeSummary = useMemo(() => {
    const complete = programmeState.data.filter(
      (programme) => programme?.setup_status === "complete"
    ).length;
    const needsReview = programmeState.data.filter(
      (programme) => programme?.setup_status === "needs_review"
    ).length;
    const initializing = programmeState.data.filter(
      (programme) => programme?.setup_status === "initializing"
    ).length;
    const evaluated = complete + needsReview;
    const coverage = evaluated > 0
      ? Math.round((complete / evaluated) * 100)
      : 0;

    return {
      total: programmeState.data.length,
      complete,
      needsReview,
      initializing,
      evaluated,
      coverage
    };
  }, [programmeState.data]);

  const recentAssessments = useMemo(
    () =>
      [...assessmentState.data]
        .sort((a, b) => {
          const aTime = new Date(a?.created_at || 0).getTime();
          const bTime = new Date(b?.created_at || 0).getTime();
          return (Number.isNaN(bTime) ? 0 : bTime) -
            (Number.isNaN(aTime) ? 0 : aTime);
        })
        .slice(0, 5),
    [assessmentState.data]
  );

  const latestAssessmentDate = recentAssessments.length
    ? formatDate(recentAssessments[0]?.created_at)
    : "Unavailable";

  return (
    <div className="dashboard-overview">
      <header className="dashboard-overview-heading">
        <h1>Administration Overview</h1>
        <span>Monitor programme setup and assessment activity.</span>
      </header>

      <section className="dashboard-summary" aria-label="Administration summary">
        <article><span>Total Programmes</span><strong>{programmeState.loading || programmeState.error ? "—" : programmeSummary.total}</strong>{programmeState.error && <small>Unavailable</small>}</article>
        <article className="is-coverage"><span>Programme Setup</span><strong>{programmeState.loading || programmeState.error ? "—" : `${programmeSummary.coverage}%`}</strong><small>{programmeState.error ? "Unavailable" : `${programmeSummary.evaluated} evaluated`}</small></article>
        <article className="is-review"><span>Programmes Needing Review</span><strong>{programmeState.loading || programmeState.error ? "—" : programmeSummary.needsReview}</strong>{programmeState.error && <small>Unavailable</small>}</article>
        <article><span>Total Assessments</span><strong>{assessmentState.loading || assessmentState.error ? "—" : assessmentState.data.length}</strong>{assessmentState.error && <small>Unavailable</small>}</article>
      </section>

      <section className="dashboard-overview-grid">
        <article className="dashboard-card programme-readiness">
          <header><div><h2>Programme Status</h2><p>Current programme setup status.</p></div><Link to="/admin/programmes">View programmes</Link></header>
          {programmeState.loading ? (
            <p className="dashboard-state">Loading programme status…</p>
          ) : programmeState.error ? (
            <p className="dashboard-state is-error" role="alert">{programmeState.error}</p>
          ) : (
            <>
              <div className="dashboard-coverage"><span style={{ width: `${programmeSummary.coverage}%` }} /></div>
              <div className="dashboard-coverage-copy"><strong>{programmeSummary.coverage}% setup complete</strong><span>{programmeSummary.complete} complete · {programmeSummary.needsReview} need review</span></div>
              <div className="readiness-counts"><span><b>{programmeSummary.complete}</b>Complete</span><span><b>{programmeSummary.needsReview}</b>Needs Review</span>{programmeSummary.initializing > 0 && <span><b>{programmeSummary.initializing}</b>Initializing</span>}</div>
              {programmeSummary.total === 0 && <p className="dashboard-state">No programmes are currently available.</p>}
            </>
          )}
        </article>

        <article className={`dashboard-card action-required ${programmeSummary.needsReview > 0 ? "has-action" : "is-clear"}`}>
          <header><div><h2>Action Required</h2><p>Programme information follow-up.</p></div></header>
          {programmeState.loading ? (
            <p className="dashboard-state">Checking programme status…</p>
          ) : programmeState.error ? (
            <p className="dashboard-state is-error" role="alert">Action status is unavailable.</p>
          ) : programmeSummary.needsReview > 0 ? (
            <div className="action-message"><strong>{programmeSummary.needsReview} programme{programmeSummary.needsReview === 1 ? "" : "s"} need review</strong><p>Provide the programme synopsis, course lists and available curriculum documents.</p><Link to="/admin/programmes">Open Programme Management</Link></div>
          ) : (
            <div className="action-message"><strong>No programme review actions are outstanding.</strong><p>All evaluated programmes currently have complete programme setup.</p><Link to="/admin/programmes">Open Programme Management</Link></div>
          )}
        </article>

        <article className="dashboard-card assessment-activity">
          <header><div><h2>Assessment Activity</h2><p>Assessment records for this university.</p></div><Link to="/admin/assessments">View assessments</Link></header>
          {assessmentState.loading ? (
            <p className="dashboard-state">Loading assessment activity…</p>
          ) : assessmentState.error ? (
            <p className="dashboard-state is-error" role="alert">{assessmentState.error}</p>
          ) : (
            <div className="activity-metrics"><span><b>{assessmentState.data.length}</b>Total records</span><span><b>{latestAssessmentDate}</b>Latest assessment date</span></div>
          )}
        </article>
      </section>

      <section className="dashboard-card recent-assessments">
        <header><div><h2>Recent Assessments</h2><p>The five latest scoped assessment records.</p></div><Link to="/admin/assessments">View all assessments</Link></header>
        {assessmentState.loading ? (
          <p className="dashboard-state">Loading recent assessments…</p>
        ) : assessmentState.error ? (
          <p className="dashboard-state is-error" role="alert">Recent assessments are unavailable.</p>
        ) : recentAssessments.length === 0 ? (
          <p className="dashboard-state">No assessment history is available for this university.</p>
        ) : (
          <div className="dashboard-table-wrap"><table><thead><tr><th>Assessment ID</th><th>Assessment Date</th><th>Personality Result</th><th>Top Recommendation</th></tr></thead><tbody>{recentAssessments.map((record) => <tr key={record.id}><td><code>{record.id ?? "Unavailable"}</code></td><td>{formatDate(record.created_at)}</td><td>{personalityLabel(record.personality_type)}</td><td>{recommendationLabel(record.top_program)}</td></tr>)}</tbody></table></div>
        )}
      </section>
    </div>
  );
}
